import { Injectable, inject, signal, computed } from '@angular/core';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

/**
 * Booking status enum matching backend status values.
 */
export type BookingStatus =
  | 'PENDING'
  | 'AWAITING_CONFIRMATION'
  | 'CONFIRMED'
  | 'STARTED'
  | 'ENDED'
  | 'CANCELLED'
  | 'RESCHEDULED'
  | 'WAITLISTED';

/**
 * Booking model as returned from the API.
 */
export interface Booking {
  booking_id: string;
  tenant_id: string;
  resource_id: string;
  client_name: string;
  client_id?: string;
  service_name: string;
  start_dt: string;       // ISO 8601
  end_dt: string;         // ISO 8601
  status: BookingStatus;
  location?: string;      // Address for field dispatch bookings
  job_lat?: number;       // GPS latitude for field dispatch
  job_lng?: number;       // GPS longitude for field dispatch
  late_start?: boolean;   // True if job started late
  job_started_at?: string;
  job_ended_at?: string;
  business_type?: string; // 'field_dispatch' or 'fixed_location'
}

export interface BookingsResponse {
  ok: boolean;
  data?: Booking[];
  bookings?: Booking[];
  error?: string;
}

/**
 * Filters bookings to today's and future dates only.
 * A booking is included if its start_dt date (date portion) is >= today's date.
 *
 * Validates: Requirements 5.1
 */
export function filterTodayAndFuture(bookings: Booking[]): Booking[] {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return bookings.filter((booking) => {
    const bookingDate = new Date(booking.start_dt);
    const bookingDateOnly = new Date(
      bookingDate.getFullYear(),
      bookingDate.getMonth(),
      bookingDate.getDate()
    );
    return bookingDateOnly >= todayStart;
  });
}

/**
 * BookingService fetches and manages bookings for the logged-in resource.
 *
 * Provides:
 * - `bookings` signal with all Booking[] data from the API
 * - `todayAndFutureBookings` computed signal filtered to today and future
 * - `loadBookings()` method to fetch from API
 * - `updateBookingStatus()` for partial WebSocket status updates
 * - `updateBooking()` for full booking replacement
 * - `addBooking()` for new allocations received via WebSocket
 * - `isLoading` signal for loading state
 * - `error` signal for error messages
 *
 * Validates: Requirements 5.1, 5.3
 */
@Injectable({ providedIn: 'root' })
export class BookingService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  /** All bookings fetched from the API (unfiltered). */
  readonly bookings = signal<Booking[]>([]);

  /**
   * Bookings filtered to today and future by default.
   * Uses start_dt date >= today's date (local time).
   *
   * Validates: Requirements 5.1
   */
  readonly todayAndFutureBookings = computed(() => {
    return filterTodayAndFuture(this.bookings());
  });

  /** Loading state. */
  readonly isLoading = signal(false);

  /** Error message from last load attempt. */
  readonly error = signal<string>('');

  /**
   * Load bookings for the logged-in resource from the API.
   */
  loadBookings(): void {
    const tenantId = this.auth.tenantId();
    const resourceId = this.auth.resourceId();

    if (!tenantId || !resourceId) {
      return;
    }

    this.isLoading.set(true);
    this.error.set('');

    this.api
      .get<BookingsResponse>(`/tenants/${tenantId}/resources/${resourceId}/bookings`)
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          if (response.ok && response.data) {
            this.bookings.set(response.data);
          } else if (response.ok && response.bookings) {
            // Legacy response format
            this.bookings.set(response.bookings);
          } else {
            this.error.set(response.error || 'Failed to load bookings');
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          this.error.set(err?.error?.error || 'Failed to load bookings');
        },
      });
  }

  /**
   * Update a single booking in the local store (e.g., from WebSocket updates).
   * Replaces the full booking object if found, otherwise adds it.
   */
  updateBooking(updatedBooking: Booking): void {
    const current = this.bookings();
    const index = current.findIndex(b => b.booking_id === updatedBooking.booking_id);
    if (index >= 0) {
      const updated = [...current];
      updated[index] = updatedBooking;
      this.bookings.set(updated);
    } else {
      this.bookings.set([...current, updatedBooking]);
    }
  }

  /**
   * Update a booking's status and optional fields via a partial update.
   * Used when WebSocket messages arrive with status changes.
   * Only updates the booking if it exists in the current list.
   *
   * Validates: Requirements 5.3
   */
  updateBookingStatus(bookingId: string, updates: Partial<Booking>): void {
    const current = this.bookings();
    const index = current.findIndex(b => b.booking_id === bookingId);

    if (index >= 0) {
      const updated = [...current];
      updated[index] = { ...updated[index], ...updates };
      this.bookings.set(updated);
    }
  }

  /**
   * Add a new booking to the list (e.g., when receiving a new allocation via WebSocket).
   * Avoids duplicates by checking booking_id.
   *
   * Validates: Requirements 5.3
   */
  addBooking(booking: Booking): void {
    const current = this.bookings();
    if (!current.some(b => b.booking_id === booking.booking_id)) {
      this.bookings.set([...current, booking]);
    }
  }
}
